import { useEffect, useRef } from 'react';
import { useAuthQuery as useQuery } from './useAuthQuery';
import { useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { Id } from '../../convex/_generated/dataModel';

/**
 * Hook to track and retrieve user login streak data.
 * Automatically records login when userId is provided.
 * Uses user's local timezone to determine the current date.
 *
 * @param userId - The Convex user ID (can be string or Id<"users">)
 */
export const useLoginStreak = (userId: Id<"users"> | string | null) => {
  const recordLogin = useMutation(api.user_metadata.recordLogin);
  const hasRecordedLogin = useRef(false);

  const streakData = useQuery(
    api.user_metadata.getLoginStreak,
    userId ? { user_id: userId } : "skip"
  );

  useEffect(() => {
    // Only record login once per hook instance and when userId is available
    if (userId && !hasRecordedLogin.current) {
      hasRecordedLogin.current = true;

      // Get local date in YYYY-MM-DD format using user's timezone
      const now = new Date();
      const localDate = now.getFullYear() + '-' +
        String(now.getMonth() + 1).padStart(2, '0') + '-' +
        String(now.getDate()).padStart(2, '0');

      const clientInfo = {
        userAgent: navigator.userAgent,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: navigator.language,
      };

      recordLogin({ user_id: userId, localDate, clientInfo }).catch((error) => {
        // Reset flag so it can retry on next render
        hasRecordedLogin.current = false;
      });
    }
  }, [userId, recordLogin]);

  return {
    currentStreak: streakData?.currentStreak || 0,
    longestStreak: streakData?.longestStreak || 0,
    lastLoginDate: streakData?.lastLoginDate ?? null,
    isLoading: streakData === undefined,
  };
};
